import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { DispatchedServiceViewModel } from '../../models/view-models/dispatched-service.view-model';
import { UUID } from 'angular2-uuid';
import { AcceptedAndNotPerformedServicesQueryService } from '../queries/accepted-and-not-performed-services-query.service';
import { CannotCompleteServicesQueryService } from '../queries/cannot-complete-services-query.service';
import { StoredStateData } from '../../storage/models/stored-state-data';
import { StateDataMapper } from '../../storage/mappers/dispatched-service-state-data-mapper';
import { GetCachedServiceByIdOperation } from 'src/app/driver-data/operations/cached-services/get-cached-service-by-id-operation';
import { GetOrRebuildStateDataByIdOperation } from 'src/app/driver-data/operations/state/get-or-rebuild-state-data-by-id-operation';

/**
 * The service responsible for maintaining and updating the current service being worked across routes.
 * @todo This service still has some functions not related to the current service. Those methods will be moved out into CQRS services over subsequent stories.
 */
@Injectable({
	providedIn: 'root'
})
export class CurrentDispatchedServiceService {
	/** The id of the current service if one has been specifically selected. */
	private currentServiceId: UUID | null = null;

	/** The mapper class for the dispatched service state data. */
	private readonly dispatchedServiceStateDataMapper: StateDataMapper = new StateDataMapper();

	/**
	 * @constructor
	 *
	 * @param getOrRebuildStateDataByIdOperation The operation for getting state data by service ID, rebuilding it if orphaned.
	 * @param getCachedServiceByIdOperation Operation service responsible for retrieving a cached service by id from the repository.
	 * @param acceptedAndNotPerformedServicesQueryService The query used to get accepted dispatched services that have not been performed.
	 * @param cannotCompleteServicesQueryService The query service used for getting could not complete services.
	 */
	constructor(
		private readonly getOrRebuildStateDataByIdOperation: GetOrRebuildStateDataByIdOperation,
		private readonly getCachedServiceByIdOperation: GetCachedServiceByIdOperation,
		private readonly acceptedAndNotPerformedServicesQueryService: AcceptedAndNotPerformedServicesQueryService,
		private readonly cannotCompleteServicesQueryService: CannotCompleteServicesQueryService
	) { }

	/**
	 * Updates the current/next Dispatched Service behavior subject.
	 */
	public GetCurrentService$(): Observable<DispatchedServiceViewModel | null> {
		if (this.currentServiceId != null ) {
			return this.GetService$(this.currentServiceId).pipe(
				switchMap((service: DispatchedServiceViewModel | null) => {
					if (service != null && !(service.HasDriverCompleted || service.HasDriverRefused)) {
						return of(service);
					}
					this.currentServiceId = null;
					return this.GetCurrentService$();
				})
			)
		}
		let service: DispatchedServiceViewModel | null = null;
		// Reactively retrieve services and determine the next service
		return this.acceptedAndNotPerformedServicesQueryService.GetAcceptedAndNotPerformedServices$().pipe(
			map((acceptedAndNotPerformed: DispatchedServiceViewModel[]) => {
				if (acceptedAndNotPerformed.length > 0) {
					const arrivedServices: DispatchedServiceViewModel[] = acceptedAndNotPerformed.filter((service: DispatchedServiceViewModel) => service.ArrivedDate != null);
					if (arrivedServices.length > 0)
						service = arrivedServices[0];
					else
						service = acceptedAndNotPerformed[0];
				}
				return service;
			}),
			switchMap((service: DispatchedServiceViewModel | null) => {
				if (service != null)
					return of(service);

				return this.cannotCompleteServicesQueryService.GetCouldNotCompletedServices$().pipe(
					map((couldNotComplete: DispatchedServiceViewModel[]) => {
						if (couldNotComplete.length > 0) {
							service = couldNotComplete[0];
						}
						return service;
					}
				));
			})
		)
	}

	/**
	 * Gets the service based on the service id.
	 * @todo Move this to a CQRS Query.
	 * @returns An observable emitting the service as a DispatchedServiceViewModel.
	 */
	public GetService$(serviceId: UUID): Observable<DispatchedServiceViewModel | null> {
		return this.getCachedServiceByIdOperation.Execute$(serviceId).pipe(
			switchMap((cachedService: DispatchedServiceViewModel | null) => {
				if (cachedService != null) {
					return this.getOrRebuildStateDataByIdOperation.Execute$(cachedService.ServiceId).pipe(
						map((stateData: StoredStateData | null) => {
							if (!stateData)
								return null;

							return this.dispatchedServiceStateDataMapper.FromStored(stateData, cachedService);
						})
					);
				}
				return of(null);
			})
		);
	}

	/**
	 * Sets the current service.
	 */
	public SetSelectedServiceId(serviceId: UUID): void {
		this.currentServiceId = serviceId;
	}

	/**
	 * Clears the current service id.
	 * This is used when the current service is actioned, but could be actioned again.
	 */
	public ClearSelectedServiceId(): void {
		this.currentServiceId = null;
	}
}